import { trpc } from '@tg/app-core';
import { GOALS, LEVELS } from '@tg/domain';
import { useNavigate } from 'react-router-dom';
import { age } from '../lib/dates';
import { useToday } from '../lib/data';
import { lsSet, useLocal } from '../lib/storage';
import { num } from '../lib/workout';
import { BackIcon, I } from '../ui/icons';
import { toast } from '../ui/overlay';
import { Dots, Head } from '../ui/parts';

/* Создание аккаунта (REG-4) — два шага. Введённое хранится черновиком на
   устройстве: назад со второго шага на первый ничего не теряет.
   Поля из карточки клиента у тренера заранее не подставляются (обновление e945f7e). */
interface Wiz {
  last: string;
  first: string;
  sex: 'm' | 'f' | '';
  born: string;
  h: string;
  w: string;
  level: string;
  goal: string;
  goal2: string;
}
const EMPTY: Wiz = { last: '', first: '', sex: '', born: '', h: '', w: '', level: '', goal: '', goal2: '' };

function useWiz() {
  const [w, setW] = useLocal<Wiz>('wiz', EMPTY);
  const set = (p: Partial<Wiz>) => setW((x) => ({ ...x, ...p }));
  return [w, set] as const;
}

/* W1. Данные — одна карточка строк «подпись — значение». Назад нет: аккаунт уже создан */
export function WizData() {
  const nav = useNavigate();
  const today = useToday();
  const [w, set] = useWiz();
  const ok = !!(w.last.trim() && w.first.trim() && w.sex && w.born && w.level);
  const opt = (val: 'm' | 'f', label: string) => (
    <button type="button" className={'opt' + (w.sex === val ? ' on' : '')} onClick={() => set({ sex: val })}>
      {label}
    </button>
  );
  return (
    <>
      <header className="top">
        <Dots n={2} at={0} />
      </header>
      <div className="pg form">
        <Head title="Ваши данные" />
        <div className="glist">
          <div className="gr">
            <label htmlFor="f-last">Фамилия</label>
            <input id="f-last" autoComplete="family-name" value={w.last} onChange={(e) => set({ last: e.target.value })} />
          </div>
          <div className="gr">
            <label htmlFor="f-first">Имя</label>
            <input id="f-first" autoComplete="given-name" value={w.first} onChange={(e) => set({ first: e.target.value })} />
          </div>
          <div className="gr">
            <label>Пол</label>
            <div className="seg2">
              {opt('m', 'Мужской')}
              {opt('f', 'Женский')}
            </div>
          </div>
          <div className="gr">
            <label htmlFor="f-born">Дата рождения</label>
            <input id="f-born" type="date" max={today} value={w.born} onChange={(e) => set({ born: e.target.value })} />
            <span className="unit">{age(w.born, today)}</span>
          </div>
          <div className="gr">
            <label htmlFor="f-h">Рост</label>
            <input id="f-h" inputMode="decimal" placeholder="по желанию" value={w.h} onChange={(e) => set({ h: e.target.value })} />
            <span className="unit">см</span>
          </div>
          <div className="gr">
            <label htmlFor="f-w">Вес</label>
            <input id="f-w" inputMode="decimal" placeholder="по желанию" value={w.w} onChange={(e) => set({ w: e.target.value })} />
            <span className="unit">кг</span>
          </div>
          <div className="gr">
            <label htmlFor="f-lvl">Уровень</label>
            <select id="f-lvl" className={w.level ? '' : 'ph'} value={w.level} onChange={(e) => set({ level: e.target.value })}>
              <option value="" disabled>
                Выберите
              </option>
              {LEVELS.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
            <I n="down" />
          </div>
        </div>
      </div>
      <div className="foot">
        <button className="btn pri" disabled={!ok} onClick={() => nav('/wiz/goals')}>
          Дальше
        </button>
      </div>
    </>
  );
}

/* W2. Цели: основная — списком, дополнительная — текстом. «Готово» → главная */
export function WizGoals() {
  const nav = useNavigate();
  const [w, set] = useWiz();
  const onboard = trpc.clientApp.onboard.useMutation();
  const utils = trpc.useUtils();
  const done = async () => {
    if (!w.goal || !w.sex) return;
    try {
      await onboard.mutateAsync({
        first: w.first.trim(),
        last: w.last.trim(),
        sex: w.sex,
        born: w.born,
        level: w.level,
        height: num(w.h),
        weight: num(w.w),
        goal: w.goal,
        goal2: w.goal2.trim() || null,
      });
      await utils.clientApp.me.invalidate();
      lsSet('wiz', undefined);
      nav('/', { replace: true });
    } catch {
      toast('Не получилось сохранить — проверьте связь');
    }
  };
  return (
    <>
      <header className="top">
        <button className="ib" onClick={() => nav('/wiz/data', { replace: true })} aria-label="Назад">
          <BackIcon />
        </button>
        <Dots n={2} at={1} />
        <span className="ib ghost" />
      </header>
      <div className="pg form">
        <Head title="Ваша цель" />
        <label className="lbl gcap" htmlFor="f-goal">
          Основная цель
        </label>
        <div className="glist sel">
          <div className="gr">
            <select id="f-goal" className={w.goal ? '' : 'ph'} value={w.goal} onChange={(e) => set({ goal: e.target.value })}>
              <option value="" disabled>
                Выберите цель
              </option>
              {GOALS.map((g) => (
                <option key={g}>{g}</option>
              ))}
            </select>
            <I n="down" />
          </div>
        </div>
        <label className="lbl gcap" htmlFor="goal2">
          Дополнительная цель <em>по желанию</em>
        </label>
        <div className="glist">
          <div className="gr col">
            <textarea id="goal2" rows={3} placeholder="Например: подтянуться 10 раз к лету" value={w.goal2} onChange={(e) => set({ goal2: e.target.value })} />
          </div>
        </div>
      </div>
      <div className="foot">
        <button className="btn pri" disabled={!w.goal || onboard.isPending} onClick={done}>
          Готово
        </button>
      </div>
    </>
  );
}
